// src/lib/audit-outcome.ts
//
// The audit trail's four-way outcome split, and the reason behind each one.
//
// ══ WHAT THIS IS FOR ══
//
// Migration `009_ledger_immutability.sql` split `outcome` from three values to
// four, because the old one conflated WHAT THE SERVER DECIDED with WHAT THE
// CLIENT WAS TOLD:
//
//   ALLOWED  2xx/3xx — permitted and performed
//   DENIED   401, 403, and a 404 that was really a SCOPE refusal
//   REFUSED  400, 409, 422, and a 404 that was really ABSENCE
//   ERROR    5xx
//
// `detail.outcomeReason` names the sub-case, and it is the entire point of the
// split: a compliance reader has to be able to tell "this caller was not
// allowed to see that bill" from "there is no such bill", and both of those are
// a 404 on the wire. Recording the two identically is what stops the API being
// an enumeration oracle; recording them identically in the LEDGER would defeat
// the audit.
//
// ══ WHY THE CLIENT VALIDATES INSTEAD OF TRUSTING ══
//
// `AuditEvent.outcome` and `detail` cross JSON untyped and are cast on arrival
// (`lib/types.ts` says so). TypeScript therefore did NOT catch the day `REFUSED`
// started arriving: `AUDIT_OUTCOME_LABEL[outcome]` returned `undefined` and the
// badge rendered an empty label with no variant. A `Record<K, V>` lookup on a
// cast value is a runtime hole wearing a compile-time type.
//
// So every function here recognises known values and has an explicit answer for
// an unknown one — the same discipline `lib/environment.ts` follows, and for the
// same reason: this file is downstream of a server that can add a fifth value
// tomorrow, and rendering nothing is the one response that must never happen on
// a compliance surface.

/** Mirrors the CHECK on `audit.event.outcome` (009_ledger_immutability.sql). */
export const AUDIT_OUTCOMES = ['ALLOWED', 'REFUSED', 'DENIED', 'ERROR'] as const
export type AuditOutcome = (typeof AUDIT_OUTCOMES)[number]

/**
 * Mirrors `AuditOutcomeReason` in `services/gateway/src/audit-hook.ts`.
 *
 * ⚠ All TEN values, not the six that carry a refusal. `ok`, `unauthenticated`,
 * `invalid` and `error` are written to real rows too, and a reader that knew
 * only the interesting ones would show a blank cell for the commonest case.
 */
export const AUDIT_OUTCOME_REASONS = [
  'ok',
  'unauthenticated',
  'forbidden',
  'scope',
  'absent',
  'conflict',
  'unprocessable',
  'invalid',
  'error',
  'unclassified',
] as const
export type AuditOutcomeReason = (typeof AUDIT_OUTCOME_REASONS)[number]

const REASON_SET: ReadonlySet<string> = new Set(AUDIT_OUTCOME_REASONS)
const OUTCOME_SET: ReadonlySet<string> = new Set(AUDIT_OUTCOMES)

export function isAuditOutcome(value: string): value is AuditOutcome {
  return OUTCOME_SET.has(value)
}

/**
 * The reason recorded on this event, or `null`.
 *
 * `null` covers two real cases and does not pretend to distinguish them,
 * because the row itself does not: an event written BEFORE migration 009 (the
 * migration's own marker row exists precisely to mark that boundary — every row
 * with a lower id was classified by the old rule), and a `detail` that carries
 * something this build does not recognise. Both render as "not recorded", which
 * is true of each.
 *
 * A THIRD case is not this one and must not be folded into it — see
 * `isDisclosureWithheld`.
 */
export function outcomeReasonOf(detail: Record<string, unknown> | undefined): AuditOutcomeReason | null {
  if (detail === undefined) return null
  const raw = detail['outcomeReason']
  if (typeof raw !== 'string' || !REASON_SET.has(raw)) return null
  return raw as AuditOutcomeReason
}

/**
 * Whether the SERVER withheld this row's grounds from this reader (S-014).
 *
 * A 404 is returned for a record outside your scope and for one that does not
 * exist, deliberately indistinguishable so the refusal cannot be used to
 * enumerate what exists. The trail records which it really was — the analyst
 * needs that — so `/api/audit` redacts it back out for a reader who is not
 * entitled to the full trail, and marks the row `outcomeDisclosure: 'withheld'`.
 *
 * This is NOT the same statement as `outcomeReasonOf(...) === null`, and
 * rendering them the same way would tell the reader the grounds were never
 * recorded when in fact they were, and are being deliberately kept from them.
 * That is the difference between a gap in the record and a working control.
 */
export function isDisclosureWithheld(detail: Record<string, unknown> | undefined): boolean {
  return detail?.['outcomeDisclosure'] === 'withheld'
}

/** Shown wherever a row's grounds were withheld. Stated as a decision, not an absence. */
export const WITHHELD_LABEL = 'Withheld: recorded, not disclosed to you'

/** Shown for a row whose grounds genuinely were never written down. */
export const NO_GROUND_LABEL = 'Grounds not recorded'

/**
 * What a row's GROUNDS line says — the three states, resolved in the one order
 * that is correct.
 *
 * ══ WHY THIS FUNCTION EXISTS AT ALL ══
 *
 * A redacted row necessarily ALSO has no recognisable `outcomeReason`: the
 * server removes the field rather than faking one (`REDACTION_MARKER_KEY` in
 * `packages/persistence/src/audit-repository.ts`). So the two states overlap in
 * the data and are told apart ONLY by asking `isDisclosureWithheld` FIRST. Ask
 * `outcomeReasonOf` first and every withheld row reports "grounds not recorded"
 * — which tells a compliance reader the trail has a hole in it, when what
 * actually happened is that a control worked. Reporting a working control as a
 * gap in the compliance record is the worst direction this page can fail in.
 *
 * That ordering lived in two places — a ternary in `StatusBadge.tsx` and a
 * `continue` in `AuditPage.tsx`'s legend — and in neither was it defended by a
 * test. Swapping either one silently produced the false report. There is now one
 * ordering, in one function, asserted directly; both call sites read it and
 * neither re-derives it — and since `StatusBadge.dom.test.tsx` landed, the
 * rendered cell is asserted in a real DOM as well, so a re-inlined ternary fails
 * at the call site too rather than only here.
 */
export type OutcomeGround =
  /** The server recorded the grounds and deliberately did not disclose them (S-014). */
  | { readonly kind: 'withheld' }
  /** The grounds are recorded and readable. */
  | { readonly kind: 'recorded'; readonly reason: AuditOutcomeReason }
  /** No grounds were ever recorded — a pre-009 row, or a `detail` this build cannot read. */
  | { readonly kind: 'not-recorded' }

export function outcomeGroundOf(detail: Record<string, unknown> | undefined): OutcomeGround {
  // ⚠ FIRST. See this type's comment: a withheld row also has no readable
  // reason, so any other order reports the redaction as a gap in the record.
  if (isDisclosureWithheld(detail)) return { kind: 'withheld' }
  const reason = outcomeReasonOf(detail)
  return reason === null ? { kind: 'not-recorded' } : { kind: 'recorded', reason }
}

/** The one line a row's grounds are rendered as, for each of the three states. */
export function outcomeGroundLabel(ground: OutcomeGround): string {
  switch (ground.kind) {
    case 'withheld':
      return WITHHELD_LABEL
    case 'recorded':
      return AUDIT_REASON_LABELS[ground.reason]
    case 'not-recorded':
      return NO_GROUND_LABEL
  }
}

/**
 * The legend's tally over the loaded window: which grounds actually occur, and
 * how many rows had theirs withheld.
 *
 * ⚠ Reads {@link outcomeGroundOf} rather than re-deciding, so the aggregate and
 * the per-row cell can never disagree about what a row is. They used to be two
 * independent walks over the same predicate pair, in two files.
 *
 * Insertion order is preserved — the server's own row order decides which ground
 * is listed first, so the legend never silently reorders what the reader sees
 * below it. `not-recorded` rows are counted in neither: they have no ground to
 * list, and a "Grounds not recorded" entry in a glossary of grounds would be a
 * vocabulary item that is not one.
 */
export interface GroundSummary {
  readonly grounds: readonly { readonly reason: AuditOutcomeReason; readonly count: number }[]
  readonly withheld: number
}

export function summariseGrounds(events: readonly { readonly detail?: Record<string, unknown> }[]): GroundSummary {
  const counts = new Map<AuditOutcomeReason, number>()
  let withheld = 0
  for (const event of events) {
    const ground = outcomeGroundOf(event.detail)
    if (ground.kind === 'withheld') withheld += 1
    else if (ground.kind === 'recorded') counts.set(ground.reason, (counts.get(ground.reason) ?? 0) + 1)
  }
  return { grounds: [...counts].map(([reason, count]) => ({ reason, count })), withheld }
}

/** The HTTP status the row recorded, when it recorded one. */
export function outcomeStatusOf(detail: Record<string, unknown> | undefined): number | null {
  if (detail === undefined) return null
  const raw = detail['status']
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : null
}

export const AUDIT_OUTCOME_LABELS: Readonly<Record<AuditOutcome, string>> = {
  ALLOWED: 'Allowed',
  REFUSED: 'Refused',
  DENIED: 'Denied',
  ERROR: 'Error',
}

/**
 * One line explaining what the outcome itself means. Shown in the page legend.
 *
 * ⚠ `REFUSED` CARRIES A CAVEAT AND IT IS NOT HEDGING. The gloss used to end
 * "— not an authorisation event", flatly, and that sentence is false for exactly
 * the rows it now sits above. `buildAuditProjection` in
 * `packages/persistence/src/audit-repository.ts` relabels a 404-resolving row to
 * `REFUSED` for a reader who is not entitled to the full trail (S-014), on
 * purpose: a scope denial and a genuine absence must read identically to that
 * reader, because telling them apart is precisely what the 404 exists to
 * prevent. So a withheld row's true outcome may well be `DENIED`/`scope` — an
 * authorisation event — and the legend above the withheld paragraph was denying
 * the very possibility that paragraph exists to disclose.
 *
 * The caveat is written so it is true whether or not any withheld rows are on
 * screen, because this map is a constant and the legend renders all four
 * outcomes unconditionally.
 */
export const AUDIT_OUTCOME_MEANING: Readonly<Record<AuditOutcome, string>> = {
  ALLOWED: 'The request was permitted and performed.',
  REFUSED:
    'Understood, and turned down on the state of the record or on domain grounds. Where the grounds are shown to you, this is not an authorisation event, but a row whose grounds are withheld from you also reads as Refused, and for that row this outcome cannot tell you which it was.',
  DENIED: 'Authorisation refused: no session, no permission, or a record outside this caller’s scope.',
  ERROR: 'The request failed inside the server.',
}

/**
 * The sub-case, in a compliance reader's language rather than the wire's.
 *
 * ⚠ `scope` and `absent` are the pair this whole split exists for, and their
 * wording is deliberately unambiguous about which is which: on the wire both
 * were a 404 with byte-identical bodies, so the ledger is the only place they
 * can be told apart, and a reader who cannot tell them apart here cannot tell
 * them apart at all.
 */
export const AUDIT_REASON_LABELS: Readonly<Record<AuditOutcomeReason, string>> = {
  ok: 'Performed',
  unauthenticated: 'No session',
  forbidden: 'Not permitted',
  scope: 'Record exists, outside this scope',
  absent: 'No such record',
  conflict: 'Conflicts with the record’s state',
  unprocessable: 'Refused on domain grounds',
  invalid: 'Malformed request',
  error: 'Server failure',
  unclassified: 'Grounds could not be established',
}

/**
 * The longer gloss, for the page legend.
 *
 * `unclassified` gets the most words because it is the one that must not be
 * read as a proven denial: the gateway records an unresolvable 404 as `DENIED`
 * on purpose (a missed denial is worse than a false one), and this text is what
 * stops an analyst counting it as evidence.
 */
export const AUDIT_REASON_MEANING: Readonly<Record<AuditOutcomeReason, string>> = {
  ok: 'Completed normally.',
  unauthenticated: 'No session was presented at all.',
  forbidden: 'A session, but the scope did not carry the authority for this action.',
  scope: 'The record is real, and this caller was not allowed to see it. The API answered exactly as it would for a record that does not exist: this row is the only place the difference is visible.',
  absent: 'There is no such record, for anybody. The identical 404 the caller received says nothing about which of these two it was.',
  conflict: 'The request was legitimate, and the record’s current state did not admit it: an approval on a bill already decided, for instance.',
  unprocessable: 'Understood and refused on domain grounds, such as no entitlement covering the service date.',
  invalid: 'The request itself was malformed.',
  error: 'A failure inside the server, not a decision about the request.',
  unclassified:
    'A refusal whose grounds could not be established. Recorded as Denied deliberately (a false denial costs one investigation, a missed one is invisible), so read this as “unknown”, never as proof that anything was refused for cause.',
}
